<script setup lang="ts">
const { t } = useI18n();
const localePath = useLocalePath();

// Peak season (Jul–Aug) rates. Keep in sync with:
// content/{en,ru,ka}/pages/guest-services.md → pool_pricing / aquapark_pricing
const rows = [
  { key: 'pool_peak',     walkin: '120 ₾', yours: '30 ₾', save: '−75%' },
  { key: 'seapool_peak',  walkin: '130 ₾', yours: '70 ₾', save: '−46%' },
  { key: 'aquapark_peak', walkin: '95 ₾',  yours: '50 ₾', save: '−47%' },
] as const;

const guestServicesHref = computed(() => localePath('/guest-services'));
</script>

<template>
  <section class="relative overflow-hidden py-10 md:py-16 bg-white dark:bg-slate-900">
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -top-32 -right-16 h-80 w-80 rounded-full bg-rose-200/30 blur-3xl dark:bg-rose-500/12"
    />
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-amber-200/25 blur-3xl dark:bg-amber-500/10"
    />
    <div data-reveal class="relative container">
      <article
        class="relative mx-auto max-w-4xl overflow-hidden rounded-3xl border border-amber-200/60 bg-gradient-to-br from-amber-50/70 via-white to-red-50/40 shadow-[0_30px_80px_-40px_rgba(212,175,55,0.25)] dark:border-amber-900/40 dark:from-amber-950/30 dark:via-slate-900 dark:to-red-950/20 dark:shadow-[0_30px_80px_-40px_rgba(251,191,36,0.15)]"
      >
        <!-- decorative atmosphere accents -->
        <div
          aria-hidden="true"
          class="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent"
        />
        <div
          aria-hidden="true"
          class="pointer-events-none absolute -top-32 -right-32 size-64 rounded-full bg-amber-400/10 blur-3xl dark:bg-amber-400/5"
        />
        <div
          aria-hidden="true"
          class="pointer-events-none absolute -bottom-24 -left-24 size-48 rounded-full bg-red-500/5 blur-3xl"
        />

        <div class="relative px-6 py-8 md:px-12 md:py-12">
          <header class="flex flex-col items-center gap-4 text-center">
            <div class="flex items-center gap-3 font-body text-[11px] uppercase tracking-[0.28em] text-amber-700 dark:text-amber-300">
              <span class="inline-block h-px w-8 bg-amber-400" aria-hidden="true" />
              <span>{{ t('prices.perks.eyebrow') }}</span>
              <span class="inline-block h-px w-8 bg-amber-400" aria-hidden="true" />
            </div>
            <h2 class="font-display text-3xl italic leading-[1.1] text-slate-900 md:text-5xl dark:text-white">
              {{ t('prices.perks.title') }}
            </h2>
            <p class="max-w-2xl font-body text-sm leading-relaxed text-slate-600 md:text-base dark:text-slate-300">
              {{ t('prices.perks.subtitle') }}
            </p>
          </header>

          <!-- rows as divide-y list inside single card -->
          <ul class="mt-10 divide-y divide-amber-200/40 border-y border-amber-200/40 dark:divide-amber-900/30 dark:border-amber-900/30">
            <li
              v-for="row in rows"
              :key="row.key"
              class="py-5 md:py-6"
            >
              <!-- Desktop: horizontal story line [label | walkin → yours | badge] -->
              <div class="hidden md:grid md:grid-cols-[1.3fr_auto_auto_auto_auto] md:items-center md:gap-6">
                <span class="font-display text-xl italic text-slate-900 dark:text-white">
                  {{ t(`prices.perks.row.${row.key}`) }}
                </span>
                <span class="font-body text-lg text-slate-400 line-through decoration-slate-400/50 tabular-nums dark:text-slate-500">
                  {{ row.walkin }}
                </span>
                <Icon name="lucide:arrow-right" class="size-4 text-amber-500/70" aria-hidden="true" />
                <span class="font-display text-3xl font-semibold italic tabular-nums text-slate-900 dark:text-white">
                  {{ row.yours }}
                </span>
                <span class="inline-flex items-center gap-1 rounded-full bg-red-500/10 px-3 py-1 font-body text-sm font-bold tabular-nums text-red-600 dark:bg-red-500/20 dark:text-red-400">
                  <Icon name="lucide:arrow-down" class="size-3.5" aria-hidden="true" />
                  {{ row.save }}
                </span>
              </div>

              <!-- Mobile: stacked, label + badge on top row; walkin → yours inline below -->
              <div class="md:hidden">
                <div class="flex items-start justify-between gap-3">
                  <span class="font-display text-lg italic leading-tight text-slate-900 dark:text-white">
                    {{ t(`prices.perks.row.${row.key}`) }}
                  </span>
                  <span class="inline-flex shrink-0 items-center gap-1 rounded-full bg-red-500/10 px-2.5 py-0.5 font-body text-xs font-bold tabular-nums text-red-600 dark:bg-red-500/20 dark:text-red-400">
                    <Icon name="lucide:arrow-down" class="size-3" aria-hidden="true" />
                    {{ row.save }}
                  </span>
                </div>
                <div class="mt-2.5 flex flex-wrap items-baseline gap-x-2 gap-y-1 font-body">
                  <span class="flex items-baseline gap-1.5">
                    <span class="text-[10px] uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
                      {{ t('prices.perks.col.walkin') }}
                    </span>
                    <span class="text-sm text-slate-400 line-through decoration-slate-400/50 tabular-nums dark:text-slate-500">
                      {{ row.walkin }}
                    </span>
                  </span>
                  <Icon name="lucide:arrow-right" class="size-3 text-amber-500/70" aria-hidden="true" />
                  <span class="flex items-baseline gap-1.5">
                    <span class="text-[10px] uppercase tracking-[0.18em] text-amber-700 dark:text-amber-400">
                      {{ t('prices.perks.col.yours') }}
                    </span>
                    <span class="font-display text-xl font-semibold italic tabular-nums text-slate-900 dark:text-white">
                      {{ row.yours }}
                    </span>
                  </span>
                </div>
              </div>
            </li>
          </ul>

          <!-- footer: footnote left, CTA right -->
          <div class="mt-8 flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between">
            <p class="font-body text-xs leading-relaxed text-slate-500 dark:text-slate-400 md:max-w-md">
              {{ t('prices.perks.footnote') }}
            </p>
            <NuxtLink
              :to="guestServicesHref"
              class="group inline-flex shrink-0 items-center gap-2 font-body text-sm font-semibold uppercase tracking-[0.14em] text-slate-800 underline-offset-[6px] decoration-amber-400 decoration-2 transition hover:text-amber-700 hover:underline dark:text-slate-100 dark:hover:text-amber-300"
            >
              {{ t('prices.perks.cta') }}
              <Icon name="lucide:arrow-up-right" class="size-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
            </NuxtLink>
          </div>
        </div>
      </article>
    </div>
  </section>
</template>
